import { useState } from 'react';
import { Link } from 'react-router-dom';

import { changeUserPassword } from '../../services/userService';
import '../Login/Login.css';
import '../RegisterPage/RegisterPage.css';
import './ChangePasswordPage.css';

const CRITERIA = {
  neutral: ['\u25CB', 'Not checked'],
  valid: ['\u2713', 'Requirement met'],
  invalid: ['\u2715', 'Requirement not met'],
};

const PasswordCriterion = ({ state, children }) => (
  <p className={'registerCriterion registerCriterion--' + state} aria-live="polite">
    <span className="registerCriterionIcon" aria-hidden="true">
      {CRITERIA[state][0]}
    </span>
    <span>{children}</span>
    <span className="registerScreenReaderText"> - {CRITERIA[state][1]}</span>
  </p>
);

const PasswordField = ({
  label,
  value,
  onChange,
  visible,
  onToggle,
  disabled,
  criterion,
}) => (
  <div className="loginField">
    <label>{label}</label>
    <div className="loginPasswordControl">
      <input
        type={visible ? 'text' : 'password'}
        autoComplete={label === 'Current password' ? 'current-password' : 'new-password'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        disabled={disabled}
      />
      <button
        className="loginPasswordToggle"
        type="button"
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        onClick={onToggle}
        disabled={disabled}
      >
        <span aria-hidden="true">{visible ? '\u25C9' : '\u25CB'}</span>
      </button>
    </div>
    {criterion}
  </div>
);

const ChangePasswordPage = () => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const newPasswordState = newPassword
    ? newPassword.length >= 12 ? 'valid' : 'invalid'
    : 'neutral';
  const confirmState = confirmPassword
    ? newPassword && newPassword === confirmPassword ? 'valid' : 'invalid'
    : 'neutral';

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    setError('');
    setMessage('');

    if (!currentPassword) return setError('Current password is required.');
    if (!newPassword || newPassword.length < 12) {
      return setError('New password must be at least 12 characters.');
    }
    if (!confirmPassword || newPassword !== confirmPassword) {
      return setError('Passwords do not match.');
    }
    if (currentPassword === newPassword) {
      return setError('New password must be different from the current password.');
    }

    setSubmitting(true);
    try {
      const result = await changeUserPassword(currentPassword, newPassword);
      setMessage(result.message);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (requestError) {
      setError(
        requestError?.response?.status === 400
          ? requestError.response.data.message
          : 'Unable to change the password. Please try again later.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="loginPage">
      <section className="loginCard changePasswordCard">
        <h1>Change password</h1>
        <form className="loginForm" onSubmit={handleSubmit} noValidate>
          <PasswordField label="Current password" value={currentPassword} onChange={setCurrentPassword} visible={showCurrentPassword} onToggle={() => setShowCurrentPassword((value) => !value)} disabled={submitting} />
          <PasswordField label="New password" value={newPassword} onChange={setNewPassword} visible={showNewPassword} onToggle={() => setShowNewPassword((value) => !value)} disabled={submitting} criterion={<PasswordCriterion state={newPasswordState}>At least 12 characters</PasswordCriterion>} />
          <PasswordField label="Confirm new password" value={confirmPassword} onChange={setConfirmPassword} visible={showConfirmPassword} onToggle={() => setShowConfirmPassword((value) => !value)} disabled={submitting} criterion={<PasswordCriterion state={confirmState}>Passwords match</PasswordCriterion>} />
          {error && <p className="loginError" role="alert">{error}</p>}
          {message && <p className="changePasswordSuccess" role="status">{message}</p>}
          <button className="loginSubmitButton" type="submit" disabled={submitting}>
            {submitting ? 'Changing password...' : 'Change password'}
          </button>
        </form>
        <p className="loginRegisterText"><Link to="/profile">Back to Profile</Link></p>
      </section>
    </main>
  );
};

export default ChangePasswordPage;
